import api, { getErrorMessage } from './api';

const marketplaceService = {
  /**
   * Get available catalog items from rewards platform (Tremendous)
   * Falls back to local marketplace catalog if rewards platform is disabled
   * @returns {Promise<Array>} - Catalog items list
   */
  getCatalog: async () => {
    try {
      // Try to get from rewards platform first (Tremendous)
      const response = await api.get('/api/v1/rewards/catalog');
      const data = response.data?.data || response.data;

      // Get conversion rate from response (default to 1.0 if not provided)
      const pointsToDollarRate = data?.points_to_dollar_rate || 1.0;

      // Transform rewards catalog items to marketplace format
      if (data?.items && Array.isArray(data.items)) {
        const items = data.items.map(item => ({
          id: item.id || item.sku,
          name: item.item_name || item.brand_name || item.name,
          description: item.description,
          points_cost: item.min_value_usd || item.points_cost || 0,
          min_value: item.min_value_usd,
          max_value: item.max_value_usd,
          currency: item.currency_code || 'USD',
          image_url: item.image_url,
          status: item.is_available !== false ? 'active' : 'inactive',
          stock: null, // External items don't have stock tracking
          sku: item.sku,
          platform: item.platform,
        }));
        // Return both items and conversion rate
        return { items, pointsToDollarRate };
      }
      return { items: [], pointsToDollarRate: 1.0 };
    } catch (error) {
      // Fall back to local marketplace catalog
      console.warn('Rewards catalog unavailable, trying local marketplace:', error.message);
      try {
        const localResponse = await api.get('/api/v1/marketplace/catalog');
        return { items: localResponse.data, pointsToDollarRate: 1.0 };
      } catch (localError) {
        throw new Error(getErrorMessage(localError));
      }
    }
  },

  /**
   * Redeem a catalog item
   * @param {string} itemId - Catalog item ID
   * @param {number} quantity - Quantity to redeem (default 1)
   * @returns {Promise<object>} - Redemption order
   */
  redeemItem: async (itemId, quantity = 1) => {
    try {
      const response = await api.post(`/api/v1/marketplace/redeem/${itemId}`, null, {
        params: { quantity },
      });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Create a new catalog item (admin only)
   * @param {object} itemData - Item data
   * @returns {Promise<object>} - Created item
   */
  createItem: async (itemData) => {
    try {
      const response = await api.post('/api/v1/marketplace/catalog', itemData);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Update a catalog item (admin only)
   * @param {string} itemId - Item ID
   * @param {object} itemData - Item data to update
   * @returns {Promise<object>} - Updated item
   */
  updateItem: async (itemId, itemData) => {
    try {
      const response = await api.put(`/api/v1/marketplace/catalog/${itemId}`, itemData);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Delete a catalog item (admin only)
   * @param {string} itemId - Item ID
   * @returns {Promise<object>} - Success response
   */
  deleteItem: async (itemId) => {
    try {
      const response = await api.delete(`/api/v1/marketplace/catalog/${itemId}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get rewards platform status
   * @returns {Promise<object>} - Status information
   */
  getRewardsStatus: async () => {
    try {
      const response = await api.get('/api/v1/rewards/status');
      return response.data?.data || response.data;
    } catch (error) {
      return { connected: false, is_enabled: false };
    }
  },

  /**
   * Sync catalog from rewards platform (admin only)
   * @returns {Promise<object>} - Sync result
   */
  syncCatalog: async () => {
    try {
      const response = await api.post('/api/v1/rewards/sync-catalog');
      return response.data?.data || response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Redeem from rewards platform (Tremendous)
   * @param {object} redemptionData - Redemption details
   * @returns {Promise<object>} - Redemption result
   */
  redeemFromPlatform: async (redemptionData) => {
    try {
      const response = await api.post('/api/v1/rewards/redeem', redemptionData);
      return response.data?.data || response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },
};

export default marketplaceService;
